import { useState } from "react";
import { toast } from "sonner";
import i18n from "@/i18n";
import {
  useDisconnectManatalIntegration,
  useManatalStatus,
  useValidateManatalIntegration,
} from "../../hooks/useManatalIntegration";
import { Loader2, CheckCircle2, Circle, Eye, EyeOff, Key } from "lucide-react";
import { Button } from "@/components/ui/atoms/Button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../ui/molecules/InputGroup";
import { MANATAL_LOGO_URL } from "../dashboard/ManatalBadge";
import { StatusPill } from "../ui/atoms/StatusPill";

interface ManatalIntegrationCardProps {
  className?: string;
}

export const ManatalIntegrationCard = ({ className = "" }: ManatalIntegrationCardProps) => {
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const { data } = useManatalStatus();
  const validateMutation = useValidateManatalIntegration();
  const disconnectMutation = useDisconnectManatalIntegration();

  const handleValidate = async () => {
    if (!apiKey.trim()) {
      toast.error(i18n.t("common:integrations.manatal_api_key_required"));
      return;
    }
    toast.loading(i18n.t("common:integrations.manatal_validating"), { id: "manatal-validate" });
    try {
      await validateMutation.mutateAsync(apiKey.trim());
      toast.success(i18n.t("common:integrations.manatal_connected"), { id: "manatal-validate" });
      setApiKey("");
      setIsEditingKey(false);
    } catch (err) {
      toast.error(
        err instanceof Error && err.message.trim() ? err.message : i18n.t("common:toasts.unexpected_error"),
        { id: "manatal-validate" }
      );
    }
  };

  const handleDisconnect = async () => {
    toast.loading(i18n.t("common:integrations.manatal_disconnecting"), { id: "manatal-validate" });
    try {
      await disconnectMutation.mutateAsync();
      toast.success(i18n.t("common:integrations.manatal_disconnected"), { id: "manatal-validate" });
      setIsEditingKey(false);
    } catch (err) {
      toast.error(
        err instanceof Error && err.message.trim() ? err.message : i18n.t("common:toasts.unexpected_error"),
        { id: "manatal-validate" }
      );
    }
  };

  const isConnected = data?.is_active;
  const isMutating = validateMutation.isPending || disconnectMutation.isPending;

  return (
    <div className={`bg-white border border-light-lila/40 rounded-xl ${className}`}>
      <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        {/* Identity */}
        <div className="flex items-center gap-3 md:min-w-[160px] md:shrink-0">
          <div className="w-10 h-10 rounded-lg bg-white border border-light-lila/50 flex items-center justify-center overflow-hidden">
            <img src={MANATAL_LOGO_URL} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-heading font-semibold text-dark-invirtus leading-tight">
              Manatal
            </div>
            <div className="text-[11px] text-dark-invirtus/50 font-body">Partner ATS</div>
          </div>
        </div>

        {/* Status pill */}
        <StatusPill
          className="hidden md:inline-flex"
          variant={isConnected ? 'success' : 'neutral'}
          icon={isConnected ? <CheckCircle2 /> : <Circle />}
        >
          {isConnected ? 'Connected' : 'Not connected'}
        </StatusPill>

        {/* API key input + inline Connect button (single bordered surface) */}
        <div className="flex-1 min-w-0 w-full flex items-center gap-2">
          {isConnected && !isEditingKey ? (
            // Connected state: masked key + Update + Disconnect, in one row
            <>
              <div className="flex-1 min-w-0 flex items-center gap-2 bg-background-gray rounded-md px-3 h-9 text-xs text-dark-invirtus/70 font-mono tracking-wider">
                <Key className="w-3.5 h-3.5 text-dark-invirtus/40 shrink-0" />
                <span className="truncate">••••••••••••••••</span>
              </div>
              <Button
                type="text"
                intent="secondary"
                size="sm"
                label="Update"
                onClick={() => setIsEditingKey(true)}
                className="shrink-0"
              />
              <Button
                onClick={handleDisconnect}
                type="outlined"
                intent="danger"
                disabled={isMutating}
                loading={isMutating && disconnectMutation.isPending}
                label="Disconnect"
              />
            </>
          ) : (
            // Not-connected / editing: InputGroup fills remaining card width
            <>
              <InputGroup size="md" className="flex-1 min-w-0">
                <InputGroupInput
                  type={showApiKey ? 'text' : 'password'}
                  rounded="full"
                  placeholder="Enter your Manatal API key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && apiKey.trim() && !isMutating) handleValidate();
                  }}
                  disabled={isMutating}
                  endAddonPadding="none"
                  className="!pr-36"
                  aria-label="Manatal API key"
                />
                <InputGroupAddon align="inline-end" className="gap-0.5">
                  <InputGroupButton
                    type="icon"
                    intent="muted"
                    icon={showApiKey ? <EyeOff /> : <Eye />}
                    onClick={() => setShowApiKey(!showApiKey)}
                    title={showApiKey ? 'Hide key' : 'Show key'}
                    aria-label={showApiKey ? 'Hide key' : 'Show key'}
                    tabIndex={-1}
                  />
                  <InputGroupButton
                    type="contained"
                    intent="secondary"
                    label={
                      isMutating && validateMutation.isPending
                        ? undefined
                        : isConnected
                          ? 'Update'
                          : 'Connect'
                    }
                    icon={
                      isMutating && validateMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : undefined
                    }
                    onClick={handleValidate}
                    disabled={isMutating || !apiKey.trim()}
                  />
                </InputGroupAddon>
              </InputGroup>
              {isEditingKey && (
                <Button
                  type="text"
                  intent="muted"
                  size="sm"
                  label="Cancel"
                  onClick={() => {
                    setIsEditingKey(false);
                    setApiKey('');
                  }}
                  className="shrink-0"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
